import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useIntl } from "react-intl";
import { SimpleModal, TemplateRow } from "src/app/components";
import { useTemplateList } from "src/app/hooks";
import { Message } from "src/app/types/Message";
import { Template } from "src/app/types/templates";
import UpsertTemplateFormContainer from "../UpsertTemplateForm/UpsertTemplateFormContainer";
import { LangType } from "src/app/types/LangType";
import { toAbsoluteUrl } from "src/app/utils/AssetHelpers";
import SVG from "react-inlinesvg";
import { TemplatesTransport } from "src/app/transports/templates";
import { toast } from "react-toastify";

export interface ITemplatesModalContainer {
    selectedRows: Message[];
    handlerValueToUpdateGrid(update: boolean): void;
    handleClose(): void;
    showModal: boolean;
}
const TemplatesModalContainer = (props: ITemplatesModalContainer) => {
    const { handleClose, showModal } = props;
    const [currentTemplate, setCurrentTemplate] = useState<Template>();
    const [loading, setLoading] = useState(false);
    const [currentLang, setCurrentLang] = useState<LangType>(LangType.FR);
    const [modalUpsert, setModalUpsert] = useState<"ADD" | "EDIT" | null>(null);
    const { templateList, refresh, loading: templateLoading } = useTemplateList(currentLang);
    const { formatMessage } = useIntl();
    const langs = Object.keys(LangType);

    useEffect(() => {
        if (!currentTemplate && templateList) {
            setCurrentTemplate(templateList[0]);
        }
    }, [templateList]);

    const handleToggle = async (template: Template) => {
        setLoading(true);
        try {
            await TemplatesTransport.toggleActive(template);
            toast.success(formatMessage({ id: "common.notification.success" }));
            refresh(currentLang);
        } catch (error: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SimpleModal
                entityName={formatMessage({ id: "common.action.template" })}
                showModal={showModal}
                handleClose={handleClose}
                formId="templates"
                loading={loading || templateLoading}
                modalProps={{ fullscreen: true }}
            >
                <div className="templatesModalWrapper">
                    <div>
                        <div className="d-flex  m-2 ">
                            <Button onClick={() => setModalUpsert("ADD")}>
                                {formatMessage({ id: "common.action.addTemplate" })}
                            </Button>
                        </div>
                        <div className="column leftColumn">
                            <div className="content">
                                {templateList?.map((template, index) => (
                                    <TemplateRow
                                        isSelected={currentTemplate?.id === template.id}
                                        key={index}
                                        template={template}
                                        onSelected={() => setCurrentTemplate(template)}
                                        onDisable={() => handleToggle(template)}
                                        onEdit={() => {
                                            setCurrentTemplate(template);
                                            setModalUpsert("EDIT");
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="d-flex m-2 justify-content-end">
                            <div className="d-flex gap-4">
                                {langs.map((lang, index) => (
                                    <div key={index} className="flagsWrapper">
                                        <Form.Check
                                            id={lang}
                                            key={index}
                                            type="radio"
                                            checked={currentLang === lang}
                                            onChange={(e) => {
                                                setCurrentLang(
                                                    LangType[
                                                        e.currentTarget
                                                            .value as keyof typeof LangType
                                                    ]
                                                );
                                                setCurrentTemplate(undefined);
                                                refresh(
                                                    LangType[
                                                        e.currentTarget
                                                            .value as keyof typeof LangType
                                                    ]
                                                );
                                            }}
                                            value={lang}
                                        />
                                        <label htmlFor={lang}>
                                            <SVG
                                                width={32}
                                                src={toAbsoluteUrl(
                                                    `/media/flags/${lang.toLowerCase()}.svg`
                                                )}
                                            />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="column rightColumn p-4">
                            <div
                                dangerouslySetInnerHTML={{ __html: currentTemplate?.text || "" }}
                            />
                        </div>
                    </div>
                </div>
            </SimpleModal>
            <UpsertTemplateFormContainer
                handleClose={() => {
                    setModalUpsert(null);
                    refresh(currentLang);
                }}
                templateList={templateList}
                currentLang={currentLang}
                template={modalUpsert === "EDIT" ? currentTemplate : undefined}
                showModal={!!modalUpsert}
            />
        </>
    );
};

export default TemplatesModalContainer;
